
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, UserCheck, Upload, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import BulkImportEAStaff from "../components/admin/BulkImportEAStaff";

export default function EAStaff() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const queryClient = useQueryClient();

  const { data: allUsers = [], isLoading } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => base44.entities.User.list(),
  });

  // Filter only EA staff
  const eaStaff = allUsers.filter(user => user.role === 'user' && user.user_type === 'ea_staff');

  const filteredStaff = eaStaff.filter(staff =>
    staff.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">EA Staff</h1>
              <p className="text-slate-600 mt-1">Manage EA Staff Accounts and Permissions</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowBulkImport(true)}
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Update Permissions
          </Button>
        </div>

        <Alert className="bg-orange-50 border-orange-200">
          <AlertCircle className="w-4 h-4 text-orange-600" />
          <AlertDescription className="text-orange-900">
            <strong>How to add EA staff:</strong> First invite users through <strong>Dashboard → Users → Invite</strong>, then use "Bulk Update Permissions" or the Users page to assign EA staff permissions.
          </AlertDescription>
        </Alert>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : filteredStaff.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                        No EA staff found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStaff.map((staff) => (
                      <TableRow key={staff.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{staff.full_name || '-'}</TableCell>
                        <TableCell>{staff.email}</TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800">
                            EA Staff
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg bg-blue-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-2">EA Staff Permissions:</h3>
            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
              <li>Take EA and ASD attendance (scan QR codes, bulk mark present/absent)</li>
              <li>View current EA and ASD rosters</li>
              <li>Process closeouts and manage daily operations</li>
              <li>Cannot access reports, student database, or user management</li>
              <li>Cannot upload grade reports or view historical data</li>
            </ul>
          </CardContent>
        </Card>

        {showBulkImport && (
          <BulkImportEAStaff 
            onClose={() => setShowBulkImport(false)}
            onSuccess={() => {
              setShowBulkImport(false);
              queryClient.invalidateQueries(['allUsers']);
            }}
          />
        )}
      </div>
    </div>
  );
}
